import './tableLegend.less'
import { IS_ALL, LIMIT_DEFAULT } from '../../constants'
import cookie from '../../services/cookieService'
import {
  CustomPaginationProps,
  LegendValuesLoadingContent
} from '../TableCardViewV2/tableCardViewV2'
import PaginationView from '../Pagination/pagination'
import { FormattedMessage, useIntl } from 'react-intl'
import React, { ReactNode } from 'react'
import { Loader, Tooltip, Typography, TypographyProps } from '@lightbeamai/design-system'

interface IProps {
  name: ReactNode
  total: number
  showing: number
  page: number
  pageSize?: number
  className?: string
  miscellaneousRecordsCount?: number
  variant?: TypographyProps['variant']
  legendValuesLoading?: boolean
  legendValuesLoadingContent?: LegendValuesLoadingContent
  hideTotalFormatted?: boolean
  // Pagination props
  showPaginatedHeader?: boolean
  pageName?: string
  totalPages?: number
  totalCount?: number
  activeTab?: string
  allowCustomise?: boolean
  customPaginationProps?: CustomPaginationProps
}

const TableLegend = (props: IProps): React.ReactElement => {
  const {
    name,
    total,
    showing,
    page,
    pageSize,
    className = '',
    miscellaneousRecordsCount = 0,
    variant = 'base',
    legendValuesLoading = false,
    legendValuesLoadingContent,
    hideTotalFormatted = false,
    // Pagination props
    showPaginatedHeader,
    pageName,
    totalPages,
    totalCount,
    activeTab,
    allowCustomise,
    customPaginationProps
  } = props
  const intl = useIntl()
  const start = (pageSize || LIMIT_DEFAULT) * (page - 1)
  // const first = start + 1
  // const last = start + showing
  const first = +page !== 1 ? start + 1 + miscellaneousRecordsCount : start + 1 //Todo - Temp fix for UX issue on Partners Page, adjusts start due to extra table entry (External Emails)
  const last = first + showing - 1
  const applyCountersCalibration = cookie.get('countersCalibration') == 'true'

  const totalCountForPagination =
    customPaginationProps?.totalItemsForPagination || totalCount || total

  /**
   * Counter calibration on UI is done to handle wrong values of list counters coming from BE (LA-3285)
   */
  const totalItems = applyCountersCalibration
    ? totalCountForPagination > last
      ? totalCountForPagination
      : last
    : totalCountForPagination
  const totalFormatted = hideTotalFormatted
    ? ''
    : intl.formatNumber(totalItems, { notation: 'compact' })

  const headerText = (
    <div className={`table-legend ${className}`} data-test-id="table-legend">
      {typeof name !== 'string' ? (
        name
      ) : (
        <>
          {last > 1 ? (
            <Typography color="placeholder" type="medium" variant={variant}>
              <FormattedMessage id="table.legend.string.3" values={{ first, last }} />{' '}
              {legendValuesLoading ? (
                <Tooltip
                  placement={'topRight'}
                  size={24}
                  title={
                    legendValuesLoadingContent && (
                      <>
                        <Typography
                          color="white"
                          variant={'baseMinus2'}
                          type={'medium'}
                          as="div"
                          className="xs-mb-8"
                        >
                          {legendValuesLoadingContent?.title || ''}
                        </Typography>
                        <Typography color="white" variant={'baseMinus2'}>
                          {legendValuesLoadingContent?.subTitle || ''}
                        </Typography>
                      </>
                    )
                  }
                >
                  <Loader loading className="table-legend-value-loader" size={16} />
                </Tooltip>
              ) : (
                totalFormatted
              )}{' '}
              {name}
            </Typography>
          ) : (
            <Typography color="placeholder" type="medium" variant={variant}>
              <FormattedMessage
                id="table.legend.string2"
                values={{ name, first, total: totalFormatted }}
              />
            </Typography>
          )}
        </>
      )}
    </div>
  )

  if (showPaginatedHeader) {
    return (
      <PaginationView
        pageName={pageName}
        currentPage={page}
        totalPages={totalPages || 0}
        pageSize={pageSize}
        total={totalCountForPagination || 1}
        activeTab={activeTab || IS_ALL}
        allowCustomise={allowCustomise}
        customPaginationProps={customPaginationProps}
        legendValuesLoading={legendValuesLoading}
        showSinglePage={true}
        singlePageText={headerText}
        totalItemsForPagination={customPaginationProps?.totalItemsForPagination}
      />
    )
  }

  if (showing) {
    return headerText
  }

  return <></>
}

export default TableLegend
